import React, { useState, useEffect } from "react";
import "./styles.css"; // Make sure to create this CSS file

const VariantPopup = ({
  title = "Add Variant",
  visible = false,
  data = {},
  getProductOptions = () => {},
  productOptions = [],
  storeOptions = [],
  cancelHandler = () => {},
  saveHandler = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    variant_name: "",
    price: 0,
    cost_price: 0,
    mrp: 0,
    weight: 0,
    unit: "gm",
    stock: 0,
    status: "active",
    is_active: 1,
    store_is_active: 1,
    store_id: "",
    product_name: "",
  });
  const [errors, setErrors] = useState({});
  const [productSearch, setProductSearch] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  useEffect(() => {
    if (visible && data) {
      // Convert display unit back to payload unit for editing
      let unitValue = data.unit || "gm";
      if (data.unit === "g") unitValue = "gm";
      if (data.unit === "kg") unitValue = "gm";
      if (data.unit === "l") unitValue = "ml";
      
      setFormData({
        variant_name: data.variant_name || "",
        price: data.price || 0,
        cost_price: data.cost_price || 0,
        mrp: data.mrp || 0,
        weight: data.weight || 0,
        unit: unitValue,
        stock: data.variant_stock || 0,
        status: data.status || "active",
        is_active: data.is_active !== undefined ? data.is_active : 1,
        store_is_active: data.store_is_active !== undefined ? data.store_is_active : 1,
        store_id: data.store_id || "",
        product_name: data.product_name || "",
      });
      setProductSearch(data.product_name || "");
      setErrors({});
    }
  }, [visible, data]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleProductSearch = (value) => {
    setProductSearch(value);
    handleInputChange("product_name", value);
    if (value.length > 0) {
      getProductOptions(value);
      setShowProductDropdown(true);
    } else {
      setShowProductDropdown(false);
    }
  };

  const selectProduct = (product) => {
    setProductSearch(product.value);
    handleInputChange("product_name", product.value);
    setShowProductDropdown(false);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.variant_name.trim()) newErrors.variant_name = "Variant name is required";
    if (!data.variant_id && !formData.product_name.trim()) newErrors.product_name = "Product is required";
    if (!formData.weight || formData.weight <= 0) newErrors.weight = "Weight must be greater than 0";
    if (!formData.unit) newErrors.unit = "Unit is required";
    if (!formData.cost_price || formData.cost_price <= 0) newErrors.cost_price = "Cost price must be greater than 0";
    if (!formData.mrp || formData.mrp <= 0) newErrors.mrp = "MRP must be greater than 0";
    if (!formData.price || formData.price <= 0) newErrors.price = "Selling price must be greater than 0";
    if (!formData.status) newErrors.status = "Status is required";
    if (storeOptions.length > 0 && !formData.store_id) newErrors.store_id = "Store is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    
    const payload = {
      variant_id: data.variant_id,
      variant_sku: data.variant_sku, 
      variant_name: formData.variant_name,
      price: parseFloat(formData.price) || 0,
      cost_price: parseFloat(formData.cost_price) || 0,
      mrp: parseFloat(formData.mrp) || 0,
      weight: parseFloat(formData.weight) || 0,
      unit: formData.unit,
      stock: parseInt(formData.stock) || 0,
      status: formData.status,
      is_active: formData.is_active,
      store_is_active: formData.store_is_active,
      store_id: formData.store_id,
    };

    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined || payload[key] === null || payload[key] === "") {
        delete payload[key];
      }
    });

    await saveHandler(payload);
    setLoading(false);
  };

  if (!visible) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content variant-modal">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="close-button" onClick={cancelHandler}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="variant-form">
          {/* Row 1: Variant Name & Product */}
          <div className="form-row">
            <div className="form-group">
              <label>
                Variant Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.variant_name}
                onChange={(e) => handleInputChange("variant_name", e.target.value)}
                placeholder="Enter variant name (e.g., 5 Kg)"
                className={errors.variant_name ? "input-error" : ""}
              />
              {errors.variant_name && <div className="error-message">{errors.variant_name}</div>}
            </div>
            
            <div className="form-group product-search-group">
              <label>
                Product {!data.variant_id && <span className="required">*</span>}
              </label>
              <input
                type="text"
                value={productSearch}
                onChange={(e) => handleProductSearch(e.target.value)}
                placeholder="Search and select product"
                disabled={!!data.variant_id}
                className={`${errors.product_name ? "input-error" : ""} ${data.variant_id ? "disabled-input" : ""}`}
                onFocus={() => productSearch && setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
              />
              {showProductDropdown && productOptions.length > 0 && (
                <div className="dropdown-menu">
                  {productOptions.map((option, index) => (
                    <div
                      key={index}
                      onClick={() => !option.disabled && selectProduct(option)}
                      className={`dropdown-item ${option.disabled ? "disabled" : ""}`}
                    >
                      {option.label || option.value}
                    </div>
                  ))}
                </div>
              )}
              {errors.product_name && <div className="error-message">{errors.product_name}</div>}
            </div>
          </div>

          {/* Row 2: Weight, Unit, Stock */}
          <div className="form-row">
            <div className="form-group">
              <label>
                Weight <span className="required">*</span>
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={formData.weight}
                onChange={(e) => handleInputChange("weight", e.target.value)}
                placeholder="Enter weight"
                className={errors.weight ? "input-error" : ""}
              />
              {errors.weight && <div className="error-message">{errors.weight}</div>}
            </div>
            
            <div className="form-group">
              <label>
                Unit <span className="required">*</span>
              </label>
              <select
                value={formData.unit}
                onChange={(e) => handleInputChange("unit", e.target.value)}
                className={errors.unit ? "input-error" : ""}
              >
                <option value="gm">Grams (g)</option>
                <option value="ml">Milliliters (ml)</option>
                <option value="pcs">Pieces (pcs)</option>
              </select>
              {errors.unit && <div className="error-message">{errors.unit}</div>}
            </div>

            <div className="form-group">
              <label>Stock Quantity</label>
              <input
                type="number"
                min="0"
                value={formData.stock}
                onChange={(e) => handleInputChange("stock", e.target.value)}
                placeholder="Enter stock"
              />
            </div>
          </div>

          {/* Row 3: Prices */}
          <div className="form-row">
            <div className="form-group">
              <label>
                Cost Price (₹) <span className="required">*</span>
              </label>
              <div className="price-input">
                <span className="currency">₹</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cost_price}
                  onChange={(e) => handleInputChange("cost_price", e.target.value)}
                  placeholder="Enter cost price"
                  className={errors.cost_price ? "input-error" : ""}
                />
              </div>
              {errors.cost_price && <div className="error-message">{errors.cost_price}</div>}
            </div>
            
            <div className="form-group">
              <label>
                MRP (₹) <span className="required">*</span>
              </label>
              <div className="price-input">
                <span className="currency">₹</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.mrp}
                  onChange={(e) => handleInputChange("mrp", e.target.value)}
                  placeholder="Enter MRP"
                  className={errors.mrp ? "input-error" : ""}
                />
              </div>
              {errors.mrp && <div className="error-message">{errors.mrp}</div>}
            </div>

            <div className="form-group">
              <label>
                Selling Price (₹) <span className="required">*</span>
              </label>
              <div className="price-input">
                <span className="currency">₹</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => handleInputChange("price", e.target.value)}
                  placeholder="Enter selling price"
                  className={errors.price ? "input-error" : ""}
                />
              </div>
              {errors.price && <div className="error-message">{errors.price}</div>}
            </div>
          </div>

          {/* Row 4: Status & Switches */}
          <div className="form-row">
            <div className="form-group">
              <label>
                Status <span className="required">*</span>
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
                className={errors.status ? "input-error" : ""}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="discontinued">Discontinued</option>
              </select>
              {errors.status && <div className="error-message">{errors.status}</div>}
            </div>

            <div className="form-group">
              <label>Variant Active</label>
              <div className="switch-container">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.is_active === 1}
                    onChange={(e) => handleInputChange("is_active", e.target.checked ? 1 : 0)}
                  />
                  <span className="slider"></span>
                </label>
                <span className="switch-label">
                  {formData.is_active === 1 ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            {storeOptions && storeOptions.length > 0 && (
              <div className="form-group">
                <label>Store Active</label>
                <div className="switch-container">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={formData.store_is_active === 1}
                      onChange={(e) => handleInputChange("store_is_active", e.target.checked ? 1 : 0)}
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="switch-label">
                    {formData.store_is_active === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Store Selection */}
          {storeOptions && storeOptions.length > 0 && (
            <div className="form-group full-width">
              <label>
                Store <span className="required">*</span>
              </label>
              <select
                value={formData.store_id}
                onChange={(e) => handleInputChange("store_id", e.target.value)}
                className={errors.store_id ? "input-error" : ""}
              >
                <option value="">Select store</option>
                {storeOptions.map((store) => (
                  <option 
                    key={store.store_id || store.value || store.id} 
                    value={store.store_id || store.value || store.id}
                  >
                    {store.name || store.label || store.store_name}
                  </option>
                ))}
              </select>
              {errors.store_id && <div className="error-message">{errors.store_id}</div>}
            </div>
          )}

          {/* Buttons */}
          <div className="modal-footer">
            <button type="button" className="cancel-button" onClick={cancelHandler}>
              Cancel
            </button>
            <button type="submit" className="save-button" disabled={loading}>
              {loading ? "Processing..." : (data.variant_id ? "Update Variant" : "Add Variant")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VariantPopup;
